import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useApiUrl } from './ApiContext';
import { BasketContext } from './BasketContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';

const Checkout = () => {
  const apiBase = useApiUrl();
  const { basket, updateQuantity, removeFromBasket } = useContext(BasketContext);

  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState('summary');
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [address, setAddress] = useState({ line1: '', line2: '', city: '', postcode: '' });
  const [comments, setComments] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const [checkoutSubmitted, setCheckoutSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [telephone, setTelephone] = useState('');
  const [telephoneTouched, setTelephoneTouched] = useState(false);
  const [telephoneValid, setTelephoneValid] = useState(false);

  const ukPostcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2})$/i;
  const ukPhoneRegex = /^(\+44\d{9,10}|0\d{9,10})$/;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailTouched(true);
    setEmailValid(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddress((a) => ({ ...a, [name]: value }));
  };

  const handleTelephoneChange = (e) => {
    const raw = e.target.value;
    setTelephone(raw);
    setTelephoneTouched(true);
    const digits = String(raw || '').replace(/[^\d+]/g, '');
    setTelephoneValid(ukPhoneRegex.test(digits));
  };

  const handleCommentsChange = (e) => setComments(e.target.value);

  const isAddressValid = () => {
    const { line1, city, postcode } = address;
    return line1.trim() !== '' && city.trim() !== '' && ukPostcodeRegex.test(postcode.trim());
  };

  const isFormValid = emailValid && isAddressValid() && privacyAccepted;

  const handleCheckoutSubmit = async () => {
    if (!isFormValid || submitting) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const payload = {
        orderType: 'Checkout',
        email,
        address: { ...address, telephone },
        comments,
        basket: basket.map((it) => ({
          name: it.name,
          quantity: Math.max(1, Number(it.quantity ?? 1)),
          qty: it.qty ?? null,
          unit: it.unit ?? null,
        })),
      };

      const url = `${apiBase.replace(/\/$/, '')}/send-checkout-email`;
      const res = await axios.post(url, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 20000,
        withCredentials: false,
        validateStatus: () => true,
      });

      if (res.status < 200 || res.status >= 300 || !res.data?.success) {
        throw new Error(res.data?.message || `Request failed (${res.status})`);
      }

      setCheckoutSubmitted(true);
      localStorage.removeItem('basket');
      setShowModal(false);

      const iv = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            clearInterval(iv);
            window.location.reload();
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setSubmitError(err?.message || 'Something went wrong sending your enquiry.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (showModal) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [showModal]);

  return (
    <div className="checkout-container">
      {!checkoutSubmitted ? (
        <div className="checkout-content">
          <h2 className="checkout-title">My Tractor</h2>

          {basket.length === 0 ? (
            <>
              <p className="checkout-empty-1">Your tractor is empty.</p>
              <p className="checkout-empty">Use the buttons below to browse products.</p>
              <div className="browse-products-wrapper">
                <Link to="/products">
                  <button>View Products</button>
                </Link>
                <Link to="/grass">
                  <button>Grass Finder</button>
                </Link>
                <Link to="/maize">
                  <button>Maize Selector</button>
                </Link>
              </div>
              <p className="checkout-empty">Need something more bespoke?</p>
              <Link to="/mixer">
                <button>Cover Crop Seed Mixer</button>
              </Link>
            </>
          ) : (
            <div className="checkout-details">
              <ul className="checkout-items">
                {basket.map((item) => (
                  <li key={item.uid || item.name} className="checkout-item">
                    <div className="checkout-item-name">{item.name}</div>

                    {item.qty && item.unit && (
                      <div className="checkout-item-line">
                        {item.qty} per {item.unit}
                      </div>
                    )}

                    <div className="checkout-quantity-controls">
                      <button
                        className="checkout-quantity-minus"
                        onClick={() =>
                          updateQuantity(item.uid || item.name, Math.max(1, (item.quantity ?? 1) - 1))
                        }
                        disabled={(item.quantity ?? 1) <= 1}
                        aria-disabled={(item.quantity ?? 1) <= 1}
                        title={(item.quantity ?? 1) <= 1 ? 'Minimum quantity is 1' : 'Decrease quantity'}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <div className="checkout-item-quantity">{item.quantity}</div>
                      <button
                        className="checkout-quantity-plus"
                        onClick={() =>
                          updateQuantity(item.uid || item.name, (item.quantity ?? 1) + 1)
                        }
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>

                    <div className="checkout-remove-container">
                      <button
                        className="checkout-remove-btn"
                        onClick={() => removeFromBasket(item.uid || item.name)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="checkout-comments-container">
                <h3>Add comments to your enquiry</h3>
                <textarea
                  maxLength="500"
                  value={comments}
                  onChange={handleCommentsChange}
                  placeholder="Add any notes or comments about your enquiry..."
                />
              </div>

              <button
                className="checkout-proceed-btn"
                onClick={() => {
                  setModalStep('summary');
                  setShowModal(true);
                }}
              >
                Enquire
              </button>
            </div>
          )}

          {showModal && (
            <div className="checkout-modal">
              <div className="checkout-modal-content">
                <button
                  className="checkout-close-modal"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                  title="Close"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>

                {modalStep === 'summary' ? (
                  <>
                    <h3 className="modal-section-title" style={{ textAlign: 'center', margin: '10px 0 8px' }}>
                      Your Enquiry Summary
                    </h3>

                    <div className="table-container">
                      <table className="checkout-summary-table" role="table">
                        <thead>
                          <tr>
                            <th scope="col" className="col-item">Item</th>
                            <th scope="col" className="col-qty">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {basket.map((item) => (
                            <tr key={item.uid || item.name}>
                              <td className="cell-item">{item.name}</td>
                              <td className="cell-num">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="checkout-comments-summary">
                      <h4>Comments</h4>
                      <p>{comments || 'No comments added.'}</p>
                    </div>

                    <button
                      className="checkout-confirm-btn"
                      onClick={() => setModalStep('details')}
                      style={{ marginTop: 16 }}
                    >
                      Confirm & Proceed
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="modal-section-title" style={{ textAlign: 'center', margin: '10px 0 8px' }}>
                      Enter Your Details
                    </h3>

                    <div className="input-group">
                      <input
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={handleEmailChange}
                        className={emailTouched ? (emailValid ? 'valid' : 'invalid') : ''}
                      />
                    </div>

                    <div className="input-group">
                      <input
                        type="text"
                        placeholder="Address Line 1"
                        name="line1"
                        value={address.line1}
                        onChange={handleAddressChange}
                        className={address.line1 ? 'valid' : ''}
                      />
                    </div>

                    <div className="input-group">
                      <input
                        type="text"
                        placeholder="Address Line 2 (Optional)"
                        name="line2"
                        value={address.line2}
                        onChange={handleAddressChange}
                      />
                    </div>

                    <div className="input-group">
                      <input
                        type="text"
                        placeholder="City"
                        name="city"
                        value={address.city}
                        onChange={handleAddressChange}
                        className={address.city ? 'valid' : ''}
                      />
                    </div>

                    <div className="input-group">
                      <input
                        type="text"
                        placeholder="Postcode"
                        name="postcode"
                        value={address.postcode}
                        onChange={handleAddressChange}
                        className={
                          address.postcode
                            ? ukPostcodeRegex.test(address.postcode.trim()) ? 'valid' : 'invalid'
                            : ''
                        }
                      />
                    </div>

                    <div className="input-group">
                      <input
                        type="tel"
                        placeholder="Telephone"
                        value={telephone}
                        onChange={handleTelephoneChange}
                        className={
                          telephoneTouched ? (telephoneValid ? 'valid' : 'invalid') : ''
                        }
                        aria-invalid={telephoneTouched && !telephoneValid}
                        aria-describedby="telHelp"
                      />
                    </div>

                    <label className="privacy-check" style={{ justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={privacyAccepted}
                        onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      />
                      <span>
                        I have read and understand the{' '}
                        <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>
                        .
                      </span>
                    </label>

                    <div className="modal-actions">
                      <button
                        type="button"
                        className="toggle-summary-btn"
                        onClick={() => setModalStep('summary')}
                        disabled={submitting}
                      >
                        Back to Summary
                      </button>

                      <button
                        className="checkout-confirm-btn"
                        disabled={!isFormValid || submitting}
                        onClick={handleCheckoutSubmit}
                      >
                        {submitting ? 'Submitting…' : 'Submit Enquiry'}
                      </button>
                    </div>

                    {submitError && (
                      <p className="form-status error" style={{ marginTop: 8 }}>{submitError}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="checkout-confirmation">
          <h1>Thank you for your enquiry!</h1>
          <p>
            Your enquiry has been placed successfully. <br />
            You will receive a confirmation email shortly with details of your enquiry.
            <br />
            Please check your junk/spam folders if you do not receive the email within a few minutes.
          </p>
          <p>Refreshing your tractor in {countdown} seconds...</p>
        </div>
      )}
    </div>
  );
};

export default Checkout;
